import React, {
  useMemo,
  useEffect,
  memo,
  ReactElement,
  useState,
  ChangeEvent,
  ReactNode,
} from 'react'
import classnames from 'classnames'
import { Radio } from 'components/forms/Radio'
import groupBy from 'lodash/groupBy'
import { Tooltip } from 'transfix-ui/components/Tooltip'
import { Box, Typography } from 'transfix-ui/core'
import 'scss/5_components/_radio-layout.scss'

export interface IRadioLayoutOption {
  label: ReactNode
  value: string
  content: ReactElement
  disabled?: boolean
  hidden?: boolean
  className?: string
  /**
   * Used for displaying header for groups,
   * and ordering groups
   * @example 'Full Truckload'
   */
  groupName?: string
  /**
   * Used for displaying tooltip text
   * when option is disabled.
   * @example 'Tendering by Routing Guide is only for Full Truckload shipments.'
   */
  tooltipText?: string
}

interface IRadioLayoutProps {
  /**
   * An array of options that are shown in the Radio Component in the sidebar.
   * This includes the component that is displayed on the right side of the
   * screen for each option.
   */
  options: IRadioLayoutOption[]
  /**
   * Show or hide the radio input for RadioLayout options
   * @default true
   */
  showRadioInput?: boolean
  /**
   * Function invoked when the visible layout has been changed, passes the
   * radio option's `value` as an argument
   */
  onChange: (radioOptionValue: string) => void
  /**
   * A pre-selected option to display
   * @default options[0]
   */
  defaultOption?: IRadioLayoutOption
  /**
   * An array of group names in order, used to sort `options`
   * @example ['First Group', 'Second Group', 'Other']
   */
  orderedGroupNames?: string[]
}

/**
 * @deprecated
 */
export const RadioLayout = memo<IRadioLayoutProps>(
  ({
    options = [],
    onChange,
    showRadioInput = true,
    defaultOption = options[0],
    orderedGroupNames = [],
  }) => {
    const filteredOptions = useMemo(() => {
      return options.filter(({ hidden }) => !hidden)
    }, [options])

    const [{ value: selectedValue }, setSelectedOption] = useState<IRadioLayoutOption>(
      defaultOption
    )

    /**
     * Updates selected option if an outside action triggers a change in the
     * default option
     */
    useEffect(() => {
      setSelectedOption(defaultOption)
    }, [defaultOption])

    /**
     * Trigger onChange prop when state changes
     */
    useEffect(() => {
      onChange(selectedValue)
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedValue])

    const selectRadioOptionHandler = ({ target: { value } }: ChangeEvent<HTMLInputElement>) => {
      const selectedOption = filteredOptions.find(option => option.value === value)
      if (selectedOption) setSelectedOption(selectedOption)
    }

    /**
     * Generates className value based on state/props
     */
    const radioOptionClass = (value: string, disabled?: boolean): string => {
      return classnames(`ph5 pv6 radio-option body-bold body-medium`, {
        'hide-radio-input': !showRadioInput,
        disabled,
        'radio-option--selected': value === selectedValue,
        'text-accent': value === selectedValue,
      })
    }

    const selectedOption = useMemo(
      () => filteredOptions.find(({ value }) => value === selectedValue),
      [selectedValue, filteredOptions]
    )

    /**
     * Group options into a keyed object
     * @example { 'Group Example': [], 'Another Group': [] }
     */
    const groupedOptions = groupBy(filteredOptions, ({ groupName = 'default' }) => groupName)

    // i.e. ['First group', 'Second group']
    const getOrderedGroupKeys = () => {
      const groupKeys = Object.keys({ ...groupedOptions }) || []
      if (!orderedGroupNames?.length) return groupKeys
      return groupKeys.sort((a, b) => {
        return orderedGroupNames.indexOf(a) - orderedGroupNames.indexOf(b)
      })
    }

    const orderedGroupKeys = getOrderedGroupKeys()

    return (
      <div className='radio-layout' data-test='radio-layout'>
        <div className='radio-layout-sidebar' data-test='radio-layout-sidebar'>
          {orderedGroupKeys.map(key => {
            const groupOptions = groupedOptions[key] || []
            return (
              <Box key={key} className='radio-layout-group' data-test='radio-layout-group'>
                {key !== 'default' && (
                  <Box className='radio-layout-header' pl={2} pr={2}>
                    <Typography variant='overline' component='span'>
                      {key}
                    </Typography>
                  </Box>
                )}
                {groupOptions.map(
                  ({ className: optionClassName, disabled, label, tooltipText, value }) => {
                    const RadioButton = (
                      <Box id={`radiobox-layout-${value}`}>
                        <Radio
                          key={value}
                          canClickLabel
                          value={value}
                          id={`radio-layout-${value}`}
                          checked={selectedValue === value}
                          label={label}
                          className={
                            !optionClassName ? radioOptionClass(value, disabled) : optionClassName
                          }
                          onChange={selectRadioOptionHandler}
                          disabled={disabled}
                        />
                      </Box>
                    )

                    return disabled && tooltipText ? (
                      <Tooltip trigger={RadioButton} position='right'>
                        {tooltipText}
                      </Tooltip>
                    ) : (
                      RadioButton
                    )
                  }
                )}
              </Box>
            )
          })}
        </div>
        <div className='radio-layout-content'>{selectedOption?.content ?? null}</div>
      </div>
    )
  }
)

export default RadioLayout
